import { Link } from "react-router";
import { ArrowLeft, HelpCircle } from "lucide-react";
import type { PortalFaqItem } from "@colab/shared";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { EmptyState } from "@/components/shared/empty-state";
import { useAdminFaq } from "./use-admin-faq";
import { FaqRows } from "./faq-rows";
import { NewFaqDialog } from "./new-faq-dialog";

// Phase 7 sub-bucket 18 — SPA port of /app/admin/portal-content/faq/page.tsx.

export function FaqListPage() {
  const query = useAdminFaq();
  const items = query.data ?? [];

  const groups = groupByCategory(items);

  return (
    <div>
      <Button asChild variant="ghost" size="sm" className="mb-2">
        <Link to="/admin/portal-content">
          <ArrowLeft className="mr-1.5 h-3.5 w-3.5" />
          Back to portal content
        </Link>
      </Button>
      <header className="mb-7 flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl font-semibold tracking-tight">
            FAQ
          </h1>
          <p className="mt-1 max-w-2xl text-sm text-muted-foreground">
            Author the FAQ that appears on /portal/dashboard and
            /portal/support. Items group by category and respect per-tier
            visibility.
          </p>
        </div>
        <NewFaqDialog />
      </header>

      {query.isLoading ? (
        <div className="space-y-3">
          {Array.from({ length: 2 }).map((_, i) => (
            <Skeleton key={i} className="h-40 w-full rounded-xl" />
          ))}
        </div>
      ) : items.length === 0 ? (
        <EmptyState
          icon={HelpCircle}
          title="No FAQ entries yet"
          description="Add a question and answer — partners will see them on the Support page."
          action={<NewFaqDialog variant="empty" />}
        />
      ) : (
        <div className="space-y-6">
          {groups.map((group) => (
            <section key={group.category} className="rounded-xl border bg-card">
              <header className="border-b p-4">
                <h2 className="font-display text-base font-semibold tracking-tight">
                  {group.category}
                </h2>
                <p className="mt-0.5 text-xs text-muted-foreground">
                  {group.items.length}{" "}
                  {group.items.length === 1 ? "entry" : "entries"}
                </p>
              </header>
              <FaqRows category={group.category} items={group.items} />
            </section>
          ))}
        </div>
      )}
    </div>
  );
}

function groupByCategory(
  items: PortalFaqItem[],
): { category: string; items: PortalFaqItem[] }[] {
  const map = new Map<string, PortalFaqItem[]>();
  for (const item of items) {
    const bucket = map.get(item.category) ?? [];
    bucket.push(item);
    map.set(item.category, bucket);
  }
  return Array.from(map.entries()).map(([category, items]) => ({
    category,
    items,
  }));
}
